import { AbstractControl, ValidatorFn } from '@angular/forms';
import { TableHeaderTypes } from '@enums/table.enum';
import { fieldTypes } from '@constants/constant';
import { isReservedFrontUrlSegment } from '@constants/reserved-front-urls';
import { REGEX } from '@core/validation';
import { environment } from '@environments/environment';
import { Fields } from '@modules/dynamic-form-builder/model/formBuilderInterface';
import { MARKETING_PAGE_SEO_TITLE_MAX_LENGTH } from '@modules/whitelabel-cover-page/marketing-page-section/marketing-page-section.constants';

export const SUB_PAGES_CONSTANTS = {
  PAGE_TITLE: 'Sub-Pages',
  ADD_SUB_PAGE: 'Add Sub-Page',
  EDIT_SUB_PAGE: 'Edit Sub-Page',
  COLUMN_NAME: 'SUB-PAGE NAME',
  COLUMN_URL: 'URL',
  COLUMN_ENABLED: 'ENABLED',
  COLUMN_ACTION: 'ACTIONS',
  STATUS_ENABLED: 'Enabled',
  STATUS_DISABLED: 'Disabled',
  NO_SUB_PAGES_FOUND: 'No Sub-Pages Found',
  EMPTY_ICON: 'assets/images/svg/empty_section_active.svg',
  ADD_SUB_PAGE_HEADING: 'Add Sub-Page',
  EDIT_SUB_PAGE_HEADING: 'Edit Sub-Page',
  SUB_PAGE_NAME_LABEL: 'Sub-Page Name',
  SUB_PAGE_NAME_PLACEHOLDER: 'Ex. About Us',
  SUB_PAGE_URL_LABEL: 'Sub-Page URL',
  SUB_PAGE_URL_PLACEHOLDER: 'about-us',
  CONFIRM_ADD_SUB_PAGE: 'ADD SUB-PAGE',
  CONFIRM_UPDATE_SUB_PAGE: 'UPDATE SUB-PAGE',
  CANCEL: 'CANCEL',
  DELETE_SUB_PAGE_HEADING: 'Delete Sub-Page',
  DELETE_SUB_PAGE_CONFIRMATION: 'Are you sure you want to delete this sub-page',
  CONFIRM_DELETE_SUB_PAGE: 'DELETE',
  SUB_PAGE_CREATED_SUCCESSFULLY: 'Sub-page created successfully',
  SUB_PAGE_UPDATED_SUCCESSFULLY: 'Sub-page updated successfully',
  SUB_PAGE_DELETED_SUCCESSFULLY: 'Sub-page deleted successfully',
  SUB_PAGE_CREATE_FAILED: 'Unable to create sub-page',
  SUB_PAGE_UPDATE_FAILED: 'Unable to update sub-page',
  SUB_PAGE_DELETE_FAILED: 'Unable to delete sub-page',
  SUB_PAGE_LOAD_FAILED: 'Unable to load sub-pages',
  SUB_PAGE_NAME_REQUIRED: 'Sub-page name is required',
  SUB_PAGE_URL_REQUIRED: 'Sub-page URL is required',
  SUB_PAGE_URL_PATTERN: 'URL must contain only lowercase letters, numbers, and hyphens',
  SUB_PAGE_URL_DUPLICATE: 'A sub-page with this URL already exists',
  VALIDATION: {
    NAME_REQUIRED: 'Sub-page name is required',
    NAME_MAX_LENGTH: 'Maximum 100 characters allowed',
    URL_REQUIRED: 'Sub-page URL is required',
    URL_PATTERN: 'Only lowercase letters, numbers, and hyphens are allowed',
    URL_MAX_LENGTH: 'Maximum 200 characters allowed',
    URL_RESERVED: 'This URL is already used. Please choose a different one'
  },
  STATUS_PUBLISHED: 'PUBLISHED',
  STATUS_DRAFT: 'DRAFT'
} as const;

export const SUB_PAGE_SECTION_KEYS = {
  NAME: 'name',
  URL: 'url',
  ENABLED: 'isEnabled',
  ACTION: 'action'
} as const;

export const SUB_PAGE_TABLE_HEADERS = [
  {
    key: SUB_PAGE_SECTION_KEYS.NAME,
    label: SUB_PAGES_CONSTANTS.COLUMN_NAME,
    width: 'w-30 w-m-100'
  },
  {
    key: SUB_PAGE_SECTION_KEYS.URL,
    label: SUB_PAGES_CONSTANTS.COLUMN_URL,
    width: 'w-30 w-m-100',
    link: true,
    showToolTip: true
  },
  {
    key: SUB_PAGE_SECTION_KEYS.ENABLED,
    label: SUB_PAGES_CONSTANTS.COLUMN_ENABLED,
    type: TableHeaderTypes.TOGGLE,
    width: 'w-20 w-m-100',
    onLabel: SUB_PAGES_CONSTANTS.STATUS_ENABLED,
    offLabel: SUB_PAGES_CONSTANTS.STATUS_DISABLED
  }
] as const;

export const SUB_PAGE_TABLE_ACTION_COLUMN = {
  label: SUB_PAGES_CONSTANTS.COLUMN_ACTION,
  width: 'w-15 w-m-100'
} as const;

export const SUB_PAGE_URL_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const SUB_PAGE_EDITOR_FORM_LABELS = {
  NAME: 'Sub-Page Name',
  URL: 'Sub-Page URL',
  SEO_TITLE: 'Sub-Page SEO Title',
  SEO_KEYWORDS: 'Sub-Page SEO Keywords (Separated by commas)',
  SEO_DESCRIPTION: 'Sub-Page SEO Description'
} as const;

export const SUB_PAGE_EDITOR_FORM_PLACEHOLDERS = {
  NAME: 'Enter Sub-Page Name',
  URL: 'Enter Sub-Page URL',
  SEO_TITLE: 'Add a Title for the Sub-Page',
  SEO_KEYWORDS: 'Add up to 20 keywords separated by commas',
  SEO_DESCRIPTION: 'Enter SEO Description'
} as const;

export const buildSubPageUrlPrefix = (): string => {
  const baseUrl = (environment.websiteURL || '').replace(/\/$/, '');
  return baseUrl ? `${baseUrl}/` : '/';
};

export const getSubPageSlug = (url: string, username = ''): string => {
  const normalized = (url || '').trim().replace(/^\/+|\/+$/g, '');
  if (!normalized) {
    return '';
  }
  const normalizedUsername = (username || '').trim().replace(/^\/+|\/+$/g, '');
  if (normalizedUsername) {
    const lowerUrl = normalized.toLowerCase();
    const lowerUsername = normalizedUsername.toLowerCase();
    if (lowerUrl.startsWith(`${lowerUsername}/`)) {
      return normalized.slice(normalizedUsername.length + 1);
    }
    if (lowerUrl.startsWith(lowerUsername)) {
      const remainder = normalized.slice(normalizedUsername.length);
      if (remainder && SUB_PAGE_URL_PATTERN.test(remainder)) {
        return remainder;
      }
    }
  }
  const segments = normalized.split('/').filter(Boolean);
  return segments[segments.length - 1] || '';
};

export const buildSubPageStoredUrl = (username: string, slugOrUrl: string): string => {
  const slug = getSubPageSlug(slugOrUrl, username);
  const normalizedUsername = (username || '').trim().replace(/^\/+|\/+$/g, '');
  if (!normalizedUsername || !slug) {
    return slug;
  }
  return `${normalizedUsername}/${slug}`;
};

export const buildSubPageFullUrl = (slugOrUrl: string, username = ''): string => {
  const slug = getSubPageSlug(slugOrUrl, username);
  if (!slug) {
    return '';
  }
  const baseUrl = (environment.websiteURL || '').replace(/\/$/, '');
  if (!baseUrl) {
    return `/${slug}`;
  }
  return `${baseUrl}/${slug}`;
};

export const reservedSubPageUrlValidator = (
  extraReserved: readonly string[] = []
): ValidatorFn => {
  return (control: AbstractControl) => {
    const value = String(control.value ?? '').trim().toLowerCase();
    if (!value) {
      return null;
    }
    if (isReservedFrontUrlSegment(value, extraReserved)) {
      return { reservedUrl: true };
    }
    return null;
  };
};

export const getSubPageEditorFormFields = (
  urlPrefix: string,
  extraReservedSlugs: readonly string[] = []
): Fields => ({
  sections: [
    {
      title: '',
      fields: [
        {
          type: fieldTypes.TEXT,
          label: SUB_PAGE_EDITOR_FORM_LABELS.NAME,
          name: 'name',
          placeholder: SUB_PAGE_EDITOR_FORM_PLACEHOLDERS.NAME,
          class: 'col-12 col-md-6',
          validation: {
            required: {
              message: SUB_PAGES_CONSTANTS.VALIDATION.NAME_REQUIRED
            },
            maxlength: {
              value: 100,
              message: SUB_PAGES_CONSTANTS.VALIDATION.NAME_MAX_LENGTH
            }
          }
        },
        {
          type: fieldTypes.TEXT,
          label: SUB_PAGE_EDITOR_FORM_LABELS.URL,
          name: 'url',
          // placeholder: SUB_PAGE_EDITOR_FORM_PLACEHOLDERS.URL,
          class: 'col-12 col-md-6 sub-page-url-field',
          socialMediaPrefix: {
            name: '',
            iconPath: '',
            url: urlPrefix
          },
          validation: {
            required: {
              message: SUB_PAGES_CONSTANTS.VALIDATION.URL_REQUIRED
            },
            maxlength: {
              value: 200,
              message: SUB_PAGES_CONSTANTS.VALIDATION.URL_MAX_LENGTH
            },
            pattern: {
              value: SUB_PAGE_URL_PATTERN,
              message: SUB_PAGES_CONSTANTS.VALIDATION.URL_PATTERN
            },
            validatorFunctions: [
              {
                validatorFn: reservedSubPageUrlValidator(extraReservedSlugs),
                message: SUB_PAGES_CONSTANTS.VALIDATION.URL_RESERVED,
                errorKey: 'reservedUrl'
              }
            ]
          }
        },
        {
          type: fieldTypes.TEXT,
          label: SUB_PAGE_EDITOR_FORM_LABELS.SEO_TITLE,
          name: 'seo_title',
          placeholder: SUB_PAGE_EDITOR_FORM_PLACEHOLDERS.SEO_TITLE,
          class: 'col-12 col-md-6',
          validation: {
            required: {
              message: SUB_PAGES_CONSTANTS.VALIDATION.NAME_REQUIRED
            },
            maxlength: {
              value: MARKETING_PAGE_SEO_TITLE_MAX_LENGTH,
              message: SUB_PAGES_CONSTANTS.VALIDATION.NAME_MAX_LENGTH
            }
          }
        },
        {
          type: fieldTypes.TEXT,
          label: SUB_PAGE_EDITOR_FORM_LABELS.SEO_KEYWORDS,
          name: 'seo_keywords',
          placeholder: SUB_PAGE_EDITOR_FORM_PLACEHOLDERS.SEO_KEYWORDS,
          class: 'col-12 col-md-6',
          validation: {
            required: {
              message: SUB_PAGES_CONSTANTS.VALIDATION.URL_REQUIRED
            },
            pattern: {
              value: REGEX.ONLY_20_COMMA_IN_STRING,
              message: 'Only 20 keywords are allowed'
            }
          }
        },
        {
          type: fieldTypes.TEXT_AREA,
          label: SUB_PAGE_EDITOR_FORM_LABELS.SEO_DESCRIPTION,
          name: 'seo_description',
          placeholder: SUB_PAGE_EDITOR_FORM_PLACEHOLDERS.SEO_DESCRIPTION,
          class: 'col-12',
          validation: {
            required: {
              message: 'SEO Description is required'
            },
            maxlength: {
              value: 1000,
              message: 'Maximum 1000 characters are allowed'
            }
          }
        }
      ]
    }
  ],
  buttons: []
});

export const SUB_PAGE_API = {
  ACTIONS: {
    CREATE_PAGE: 'create_page',
    UPDATE_PAGE: 'update_page',
    DELETE_PAGE: 'delete_page',
    CREATE_SECTION: 'create_section',
    UPDATE_SECTION: 'update_section',
    DELETE_SECTION: 'delete_section',
    REORDER_SECTIONS: 'reorder_sections'
  },
  FIELDS: {
    ACTION: 'action',
    PAGE_ID: 'page_id',
    SECTION_ID: 'section_id',
    USER_ID: 'user_id',
    URL: 'url',
    TITLE: 'title',
    STATUS: 'status',
    IS_ENABLED: 'is_enabled',
    SEQUENCE_NUMBER: 'sequence_number',
    SECTIONS: 'sections',
    PREVIEW: 'preview',
    CREATED_AT: 'created_at',
    UPDATED_AT: 'updated_at',
    META_TITLE: 'meta_title',
    META_DESCRIPTION: 'meta_description',
    SEO_KEYWORDS: 'seoKeywords',
    LAYOUT_ID: 'layout_id',
    SECTION_TYPE: 'section_type',
    IS_VISIBLE: 'is_visible'
  },
  QUERY: {
    USER_ID: 'user_id',
    URL: 'url',
    PAGE_ID: 'page_id',
    SECTION_ID: 'section_id',
    PREVIEW: 'preview'
  }
} as const;
